import type { Ref } from 'vue'

import type { CustomFieldDefinition, CustomPropertyValue, CustomPropertyValueValue } from '/@admin:types'

export function wait(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms))
}

export function takeAtLeast<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    Promise.all([p, wait(ms)]).then(([result]) => {
      resolve(result)
    }, reject)
  })
}

export const ucfirst = (str: string, locale = 'nl') =>
  str.replace(/^\p{CWU}/u, (char) => char.toLocaleUpperCase(locale))

export async function scrollErrorIntoView() {
  await wait(200)

  const el = document.querySelector('.form-error') as HTMLElement
  if (el) {
    el.closest('.field')?.scrollIntoView({
      behavior: 'smooth',
    })
  }
}

type FilteredErrors = Record<string, string>

export const filterErrorsByPrefix = (errors: Partial<Record<string, string>>, prefix: string): FilteredErrors => {
  if (!prefix) {
    return errors as FilteredErrors
  }

  const results: FilteredErrors = {}
  Object.keys(errors).forEach((key) => {
    if (key.startsWith(`${prefix}.`)) {
      results[key.substring(prefix.length + 1)] = String(errors[key])
    }
  })

  return results
}

export const processCustomProperties = (
  customFields: CustomFieldDefinition[],
  items: CustomPropertyValue[] | Record<string, CustomPropertyValueValue>,
): Record<string, CustomPropertyValue> => {
  const results: Record<string, CustomPropertyValue> = {}

  customFields.forEach((f) => {
    let value: CustomPropertyValueValue = f.default ?? ''

    // in case 'items' is a key/value dictionary
    if (Object.hasOwn(items, f.id)) {
      value = (items as Record<string, CustomPropertyValueValue>)[f.id]
    }

    switch (f.type) {
      case 'multiselect':
        value = []
        break
    }

    results[f.id] = {
      definition: f.id,
      value: value,
    }
  })

  if (Array.isArray(items)) {
    items.forEach((item) => {
      results[item.definition] = item
    })
  }

  return results
}

export const setActiveTabFromHash = (activeTab: Ref<string>) => {
  if (location.hash.substring(1, 3) !== 't:') {
    return
  }

  activeTab.value = location.hash.substring(3)

  setTimeout(() => {
    history.pushState('', document.title, window.location.pathname + window.location.search)
  }, 20)
}

export const switchTabsOnHashChange = (activeTab: Ref<string>) => {
  if (!window) {
    return
  }

  window.addEventListener('hashchange', () => {
    setActiveTabFromHash(activeTab)
  })
}

export const filesize = (bytes: number, fixed?: number): string => {
  const unit = ['b', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb', 'Eb', 'Zb', 'Yb']
  const radix = 1024

  if (fixed === undefined) {
    fixed = 1
  }

  let loop = 0
  // calculate
  while (bytes >= radix) {
    bytes /= radix
    ++loop
  }
  return bytes.toFixed(fixed) + ' ' + unit[loop]
}

export async function loadScript(url: string) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = url
    script.addEventListener('load', () => resolve(script), false)
    script.addEventListener('error', () => reject(script), false)
    document.body.appendChild(script)
  })
}
